/**
 * WHERE FIREBASE LOOKS FOR THE SIGNED-IN PERSON ON BOOT (2026-10-31, the sign-in stalls).
 *
 * The finding, from the web-vitals record of 2026-10-24 to 10-31: about one page view in 22 waited 10 s or more for
 * sign-in to say who was here, and half of those waited on Firebase's own browser database (`firebaseLocalStorageDb`),
 * mostly when several prest0 tabs loaded at once. Read from node_modules/@firebase/auth (PersistenceUserManager.create):
 * before its first answer, Firebase checks EVERY store in the order it was created with. It opens the browser
 * database, writes and deletes a test record, and afterwards deletes the person's key from it, even though the person
 * is kept in local storage, first in that order (utils/firebase.ts, 2026-09-23). When the browser database is slow or
 * held, sign-in waits with it.
 *
 * So the order is chosen at boot from what local storage already holds:
 *   - someone is signed in (their record is in local storage): local storage, then the per-tab session store. Firebase
 *     never opens its browser database on the way to its first answer;
 *   - nobody is in local storage (a visitor, someone signed out, or someone last here before 2026-09-23, whose record
 *     the SDK's old default kept in the browser database): the full order, so that record is still found, and Firebase
 *     moves it into local storage as it always has.
 * Local storage is first either way, so a stored person is never moved on boot (tests/auth/authStoreOrder.test.ts).
 *
 * A leaf with no imports: utils/firebase.ts maps these names onto the SDK's stores, and the Chromium probe
 * (scripts/probe-auth-held-database.mjs) runs this same file against the real SDK.
 */

export type AuthStoreName = 'local' | 'indexedDB' | 'session';

/** The key Firebase keeps a signed-in person under (the SDK's own `_persistenceKeyName`). */
export function authUserStorageKey(apiKey: string, appName: string = '[DEFAULT]'): string {
  return `firebase:authUser:${apiKey}:${appName}`;
}

/** Does local storage hold a signed-in person for this app? A store that cannot be read answers no. */
export function userStoredLocally(
  apiKey: string | undefined,
  storage: Pick<Storage, 'getItem'> | null | undefined,
): boolean {
  if (!apiKey || !storage) return false;
  try {
    return storage.getItem(authUserStorageKey(apiKey)) !== null;
  } catch {
    return false;
  }
}

/** The stores Firebase boots with, in order. */
export function authStoresAtBoot(storedLocally: boolean): AuthStoreName[] {
  return storedLocally ? ['local', 'session'] : ['local', 'indexedDB', 'session'];
}
